<template>
  <Drawer
    :open="open"
    :title="t('calendar.title')"
    @close="$emit('close')"
  >
    <p v-if="!hasJourney" class="fg-empty">{{ t('calendar.empty') }}</p>

    <div v-if="hasJourney && isReturn" class="fg-cal__tabs">
      <button
        type="button"
        class="fg-cal__tab"
        :class="{ 'is-active': mode === 'out' }"
        @click="mode = 'out'"
      >
        <span class="fg-cal__tab-label">{{ t('calendar.tabOutward') }}</span>
        <span class="fg-cal__tab-value">{{ outDate ? formatLong(outDate) : t('calendar.tabSelect') }}</span>
      </button>
      <button
        type="button"
        class="fg-cal__tab"
        :class="{ 'is-active': mode === 'ret', 'is-disabled': !outDate }"
        :disabled="!outDate"
        @click="mode = 'ret'"
      >
        <span class="fg-cal__tab-label">{{ t('calendar.tabReturn') }}</span>
        <span class="fg-cal__tab-value">{{ retDate ? formatLong(retDate) : t('calendar.tabSelect') }}</span>
      </button>
    </div>

    <div v-if="hasJourney" class="fg-cal">
      <header class="fg-cal__nav">
        <button type="button" class="fg-btn fg-btn--icon" :disabled="!canPrevMonth" @click="cursorMonth = addMonths(cursorMonth, -1)">
          <Icon name="chevron-left" :size="16" />
        </button>
        <span class="fg-cal__nav-title">{{ formatMonthYear(cursorMonth) }}</span>
        <button type="button" class="fg-btn fg-btn--icon" @click="cursorMonth = addMonths(cursorMonth, 1)">
          <Icon name="chevron-right" :size="16" />
        </button>
      </header>

      <div class="fg-cal__weekdays">
        <span v-for="key in WEEKDAY_KEYS" :key="key">{{ t(key) }}</span>
      </div>

      <p v-if="availableDates.size === 0" class="fg-empty">{{ t('calendar.noDepartures') }}</p>
      <div v-else class="fg-cal__grid">
        <button
          v-for="(cell, i) in cells"
          :key="i"
          type="button"
          class="fg-cal__cell"
          :class="cellClasses(cell)"
          :disabled="!cell || !isSelectable(cell.iso)"
          @click="cell && pickDate(cell.iso)"
        >
          <span v-if="cell">{{ cell.day }}</span>
        </button>
      </div>

      <!-- Récap compact des choix : aller (toujours visible) + retour (se
           complète au fil de la sélection) avec les départs disponibles. -->
      <section v-if="outDate" class="fg-cal__recap">
        <div class="fg-cal__recap-leg" :class="{ 'is-active': mode === 'out' }">
          <div class="fg-cal__recap-head">
            <Icon name="arrow-right" :size="12" />
            <span class="fg-cal__recap-label">{{ t('calendar.tabOutward') }}</span>
            <span class="fg-cal__recap-date">{{ formatLong(outDate) }}</span>
          </div>
          <div v-if="outwardTimes.length" class="fg-cal__recap-times">
            <span v-for="tm in outwardTimes" :key="tm" class="fg-cal__recap-time">{{ tm }}</span>
          </div>
        </div>

        <div v-if="isReturn" class="fg-cal__recap-leg" :class="{ 'is-active': mode === 'ret' }">
          <div class="fg-cal__recap-head">
            <Icon name="arrow-left" :size="12" />
            <span class="fg-cal__recap-label">{{ t('calendar.tabReturn') }}</span>
            <span class="fg-cal__recap-date" :class="{ 'is-muted': !retDate }">
              {{ retDate ? formatLong(retDate) : t('calendar.tabSelect') }}
            </span>
          </div>
          <div v-if="returnTimes.length" class="fg-cal__recap-times">
            <span v-for="tm in returnTimes" :key="tm" class="fg-cal__recap-time">{{ tm }}</span>
          </div>
        </div>
      </section>

      <!-- Aller simple + aller choisi → proposer d'ajouter un retour
           (bascule en aller-retour et ouvre le choix de la date retour). -->
      <button
        v-if="!isReturn && outDate && hasReturnDates"
        type="button"
        class="fg-cal__add-return"
        @click="addReturn"
      >
        <Icon name="plus" :size="14" />
        {{ t('calendar.addReturn') }}
      </button>
    </div>

    <template v-if="hasJourney" #footer>
      <button class="fg-btn fg-btn--primary fg-btn--block" type="button" :disabled="!canConfirm" @click="$emit('close')">
        {{ t('common.confirm') }}
      </button>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useStore } from '../../store'
import { useI18n } from '../../i18n'
import { Direction } from '../../../../shared/types/enums'
import type { Journey, RequestItem, SearchState } from '../../../../shared/types'
import {
  buildMonthCells,
  collectAvailableDates,
  dedupDepartures,
  isDateSelectable as isDateSelectablePure,
  toIsoDate,
} from '../../../../shared/rules'
import Drawer from '../Drawer.vue'
import Icon from '../Icon.vue'

interface DayCell { day: number; iso: string; inPast: boolean }

const WEEKDAY_KEYS = [
  'calendar.weekday.mon', 'calendar.weekday.tue', 'calendar.weekday.wed',
  'calendar.weekday.thu', 'calendar.weekday.fri', 'calendar.weekday.sat',
  'calendar.weekday.sun',
] as const

const props = defineProps<{ open: boolean }>()
defineEmits<{ close: [] }>()

const store = useStore()
const { t, locale } = useI18n()

const mode = ref<'out' | 'ret'>('out')
const cursorMonth = ref(new Date())

const hasJourney = computed(() => store.search.journeys.length > 0)
const isReturn = computed(() => store.search.direction === Direction.Return)

const outDate = computed(() => readDate('out_at'))
const retDate = computed(() => readDate('ret_at'))

const availableDates = computed<Set<string>>(() => {
  const direction = mode.value === 'out' ? Direction.Outward : Direction.Return
  return collectAvailableDates(store.search.journeys, direction)
})

interface DepartureDetail { company: string; logo: string; time: string }

// Détail des départs d'un jour pour une direction donnée : compagnie + heure,
// dédupliqué (un même ferry partagé entre 2 journey IDs n'apparaît qu'une fois).
function detailsForDate(date: string | null, direction: Direction): DepartureDetail[] {
  if (!date) return []
  const details: DepartureDetail[] = []
  for (const journey of store.search.journeys) {
    if (journey.direction !== direction) continue
    const company = typeof journey.company === 'object' && journey.company !== null
      ? (journey.company as { name?: string; logo?: string })
      : { name: typeof journey.company === 'string' ? journey.company : '' }
    const companyName = company.name ?? ''
    const logo = (company as { logo?: string }).logo ?? ''
    for (const dep of journey.departure ?? []) {
      // `dep.depart` déjà au format `YYYY-MM-DD` → comparaison directe.
      if (!dep?.depart || dep.depart.slice(0, 10) !== date) continue
      details.push({ company: companyName, logo, time: dep.time ?? '' })
    }
  }
  return dedupDepartures(details).sort((a, b) => a.time.localeCompare(b.time))
}

// Heures uniques disponibles pour un jour/direction → chips compactes du récap.
function uniqueTimes(details: DepartureDetail[]): string[] {
  return Array.from(new Set(details.map((d) => d.time).filter(Boolean))).sort((a, b) => a.localeCompare(b))
}

const outwardTimes = computed(() => uniqueTimes(detailsForDate(outDate.value, Direction.Outward)))
const returnTimes = computed(() => uniqueTimes(detailsForDate(retDate.value, Direction.Return)))

// Y a-t-il des départs RETOUR sur cette route ? Conditionne l'offre « Ajouter
// un retour » (inutile de la proposer si la route n'a pas de retour, ex. AML).
const hasReturnDates = computed(() => collectAvailableDates(store.search.journeys, Direction.Return).size > 0)

const canConfirm = computed(() => {
  if (!outDate.value) return false
  return !isReturn.value || !!retDate.value
})

const todayIso = computed(() => toIsoDate(new Date()))
const canPrevMonth = computed(() => {
  const first = new Date(cursorMonth.value.getFullYear(), cursorMonth.value.getMonth(), 1)
  return first > new Date(new Date().getFullYear(), new Date().getMonth(), 1)
})

const cells = computed<(DayCell | null)[]>(() => {
  const year = cursorMonth.value.getFullYear()
  const month = cursorMonth.value.getMonth()
  return buildMonthCells(year, month).map((d) => {
    if (d === null) return null
    const iso = toIsoDate(new Date(year, month, d))
    return { day: d, iso, inPast: iso < todayIso.value }
  })
})

watch(() => props.open, (isOpen) => {
  if (!isOpen) return
  mode.value = 'out'
  const anchor = outDate.value ?? findFirstAvailable() ?? toIsoDate(new Date())
  cursorMonth.value = parseIso(anchor)
})

watch(outDate, (date) => {
  if (date && isReturn.value && !retDate.value) mode.value = 'ret'
})

// Aller simple → aller-retour depuis le calendrier : bascule la direction et
// ouvre directement l'onglet retour pour choisir la date.
function addReturn(): void {
  store.search.direction = Direction.Return
  mode.value = 'ret'
}

function pickDate(iso: string): void {
  if (mode.value === 'out') {
    setDateForAll('out_at', iso)
    if (retDate.value && retDate.value < iso) setDateForAll('ret_at', '')
    if (isReturn.value && !retDate.value) mode.value = 'ret'
  } else {
    setDateForAll('ret_at', iso)
  }
}

function isSelectable(iso: string): boolean {
  return isDateSelectablePure(iso, {
    mode: mode.value,
    outDate: outDate.value,
    availableDates: availableDates.value,
    todayIso: todayIso.value,
  })
}

function cellClasses(cell: DayCell | null): Record<string, boolean> {
  if (!cell) return {}
  const isOut = cell.iso === outDate.value
  const isRet = cell.iso === retDate.value
  const inRange = !!(outDate.value && retDate.value && cell.iso > outDate.value && cell.iso < retDate.value)
  return {
    'fg-cal__cell--available': isSelectable(cell.iso),
    'fg-cal__cell--past': cell.inPast,
    'fg-cal__cell--start': isOut,
    'fg-cal__cell--end': isRet,
    'fg-cal__cell--in-range': inRange,
    'fg-cal__cell--today': cell.iso === todayIso.value,
  }
}

function readDate(field: 'out_at' | 'ret_at'): string | null {
  for (const items of Object.values(store.search.request)) {
    for (const item of items) {
      const value = item[field]
      if (value) return value
    }
  }
  return null
}

function setDateForAll(field: 'out_at' | 'ret_at', value: string): void {
  ensureRequestItems(store.search)
  for (const items of Object.values(store.search.request)) {
    for (const item of items) item[field] = value
  }
}

function ensureRequestItems(state: SearchState): void {
  if (Object.keys(state.request).length > 0) return
  // Dédup par (company + route) — le backend renvoie parfois plusieurs
  // journey_id distincts pour la même paire (compagnie, origin, destination)
  // (ex: codes commerciaux ALN-NDR vs ANR-NDR). On garde le premier → 1 seul
  // appel /api/availability par compagnie réelle.
  const seen = new Set<string>()
  for (const outward of state.journeys.filter((j) => j.direction === Direction.Outward)) {
    const company = resolveCompany(outward)
    const dedupKey = `${company}|${outward.origin?.port_code ?? ''}|${outward.destination?.port_code ?? ''}`
    if (seen.has(dedupKey)) continue
    seen.add(dedupKey)
    const ret = findReturnFor(outward, state.journeys)
    state.request[company] ??= []
    state.request[company].push(emptyRequestItem(company, outward, ret))
  }
}

function findReturnFor(outward: Journey, journeys: Journey[]): Journey | null {
  // Le retour DOIT être de la MÊME compagnie que l'aller : sinon on apparie un
  // retour d'un autre provider (ex. aller Balearia + retour GNV) booké sur la
  // gateway de l'aller → dossier incohérent (aller balearia / retour GNV).
  const company = resolveCompany(outward)
  return journeys.find((j) =>
    j.direction === Direction.Return
    && resolveCompany(j) === company
    && j.origin?.port_code === outward.destination?.port_code
    && j.destination?.port_code === outward.origin?.port_code,
  ) ?? null
}

function resolveCompany(journey: Journey): string {
  return typeof journey.company === 'string' ? journey.company : journey.company?.name ?? 'default'
}

function emptyRequestItem(company: string, outward: Journey, ret: Journey | null): RequestItem {
  return {
    company,
    depart_code: outward.origin?.port_code,
    arrive_code: outward.destination?.port_code,
    out_journey_id: outward.id,
    out_at: '',
    out_is_open: false,
    ret_journey_id: ret?.id,
    ret_at: '',
    ret_is_open: false,
  }
}

function findFirstAvailable(): string | null {
  let earliest: string | null = null
  for (const iso of availableDates.value) {
    if (iso < todayIso.value) continue
    if (!earliest || iso < earliest) earliest = iso
  }
  return earliest
}

function parseIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1)
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1)
}

function formatLong(iso: string): string {
  return parseIso(iso).toLocaleDateString(locale.value, { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatMonthYear(d: Date): string {
  return d.toLocaleDateString(locale.value, { month: 'long', year: 'numeric' })
}
</script>
